import { useState, useRef, useEffect, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, parseFieldErrors, parseErrorMessage, resetSessionCache } from "@/lib/queryClient";
import { consumeReturnTo, consumeLifetimeReturnTo } from "@/lib/auth-return-to";
import { useToast } from "@/hooks/use-toast";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import logoImg from "@assets/Padpulse_logo_horizontal_nobg.png";
import { applyThemeClass } from "@/lib/theme";
import { usePageTitle } from "@/hooks/use-page-title";
import { needsPaymentSetupFromResponse } from "@shared/subscription-gate";

interface VerifyResponse {
  success: boolean;
  requiresTotp?: boolean;
  requiresTotpEnrollment?: boolean;
  pendingInvitationCount?: number;
  activeMembership?: { ownerUserId: number; ownerEmail: string; role: string } | null;
  isBillableAccountOwner?: boolean;
  user?: {
    id: number;
    email: string;
    name: string | null;
    approved: boolean;
    status?: string;
    phone?: string | null;
    onboardingCompletedAt?: string | null;
    onboardingWizardCompletedAt?: string | null;
    preferredTheme?: string;
    hasPaymentMethod?: boolean;
    needsPaymentSetup?: boolean;
    tosAcknowledgedAt?: string | null;
  };
}

export default function AppVerify() {
  usePageTitle("Verify Email");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [codeError, setCodeError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const email = sessionStorage.getItem("padpulse_auth_email") || "";
  const signupDataRaw = sessionStorage.getItem("padpulse_signup_data");
  const signupData = signupDataRaw ? JSON.parse(signupDataRaw) : null;
  const isSignup = !!signupData;
  const hasPassword = !isSignup && sessionStorage.getItem("padpulse_auth_has_password") === "1";

  const [sessionProbeState, setSessionProbeState] = useState<"checking" | "ready">("checking");
  const probingRef = useRef(false);
  const navigatedRef = useRef(false);

  const probeSession = useCallback(async () => {
    if (probingRef.current || navigatedRef.current) return;
    probingRef.current = true;
    try {
      const res = await fetch("/api/app/profile", { credentials: "include" });
      if (res.ok && !navigatedRef.current) {
        navigatedRef.current = true;
        const profile = await res.json().catch(() => null);
        resetSessionCache();
        navigate(consumeReturnTo(profile?.id ?? null) ?? "/app/dashboard");
        return;
      }
    } catch {
      // Network error — fall through to show the form
    } finally {
      probingRef.current = false;
    }
    setSessionProbeState("ready");
  }, [navigate]);

  useEffect(() => {
    probeSession();
  }, [probeSession]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        probeSession();
      }
    };
    const handleFocus = () => {
      probeSession();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [probeSession]);

  useEffect(() => {
    if (sessionProbeState === "ready" && !email && !navigatedRef.current) {
      navigate("/app/login");
    }
  }, [sessionProbeState, email, navigate]);

  const verifyMutation = useMutation<VerifyResponse, Error, string>({
    mutationFn: async (fullCode: string) => {
      if (isSignup) {
        const res = await apiRequest("POST", "/api/app/auth/verify-signup", {
          email,
          code: fullCode,
          ...signupData,
        });
        return await res.json() as VerifyResponse;
      } else {
        const res = await apiRequest("POST", "/api/app/auth/verify-code", { email, code: fullCode });
        return await res.json() as VerifyResponse;
      }
    },
    onSuccess: (data) => {
      setCodeError(null);
      sessionStorage.removeItem("padpulse_auth_email");
      sessionStorage.removeItem("padpulse_signup_data");
      sessionStorage.removeItem("padpulse_auth_has_password");
      resetSessionCache();
      if (data?.requiresTotpEnrollment) {
        navigate("/app/totp-enroll");
        return;
      }
      if (data?.requiresTotp) {
        navigate("/app/totp-verify");
        return;
      }
      const u = data?.user;
      const lifetimeReturn = consumeLifetimeReturnTo(u?.id ?? null);
      if (lifetimeReturn) { navigate(lifetimeReturn); return; }
      if (u?.preferredTheme === "dark" || u?.preferredTheme === "light" || u?.preferredTheme === "system") {
        applyThemeClass(u.preferredTheme);
      }
      // Self-serve signup: the server auto-approves every verified account,
      // so no branch routes to /app/awaiting-approval anymore.
      if (!u?.phone) {
        navigate("/app/onboarding");
      } else if ((data?.pendingInvitationCount ?? 0) > 0) {
        // A user with pending team invitations must resolve them BEFORE the
        // credit-card gate. A freshly-invited user has no *accepted*
        // membership yet, so `isBillableAccountOwner` is still true and would
        // otherwise push them to the payment page before they ever see the
        // accept-invitations screen (Task #4154). Accepting makes them a team
        // member billed under the owner (no card); declining all of them
        // leaves a billable owner who then correctly hits the card page.
        navigate("/app/accept-invitations");
      } else if (
        u?.approved &&
        // A saved card alone is not "paid" on modular pricing — owners go
        // through Checkout until a live subscription exists (the server's
        // `needsPaymentSetup`). A lapsed owner lands on the payment page's
        // "trial ended" variant, which also links to the free P&L.
        needsPaymentSetupFromResponse(u) &&
        // Only the billable account owner is asked for a card. Team members
        // (activeMembership !== null) and resident members must skip this
        // step entirely (Task #1378).
        (data?.isBillableAccountOwner ?? !data?.activeMembership)
      ) {
        navigate("/app/onboarding/payment");
      } else if (!u?.onboardingWizardCompletedAt) {
        // Task #1630 — Gate on the wizard-finished flag, not the bot-owned
        // `onboardingCompletedAt`.
        navigate("/app/welcome");
      } else {
        // Phase 3 — only the dashboard-default branch honors a stashed deep
        // link; every onboarding/approval branch above keeps precedence. The
        // stash is bound to the user it was captured for.
        navigate(consumeReturnTo(u?.id ?? null) ?? "/app/dashboard");
      }
    },
    onError: (err: Error) => {
      const fieldErrs = parseFieldErrors(err);
      const inline = fieldErrs.code || Object.values(fieldErrs)[0];
      if (inline) {
        setCodeError(inline);
        toast({ title: "Please fix the highlighted field", description: inline, variant: "destructive" });
      } else {
        const message = parseErrorMessage(err, "The code you entered is incorrect or expired.");
        setCodeError(message);
        toast({ title: "Invalid code", description: message, variant: "destructive" });
      }
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    },
  });

  const handleChange = (index: number, value: string) => {
    // A multi-character value means a password manager (1Password) or browser
    // autofill dropped the whole code into one box without firing a paste
    // event. Strip any separators (e.g. "123 456") and distribute the digits
    // across all boxes so autofill fills the whole code.
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 6);
      if (!digits) return;
      if (codeError) setCodeError(null);
      const newCode = ["", "", "", "", "", ""];
      for (let i = 0; i < 6; i++) newCode[i] = digits[i] || "";
      setCode(newCode);
      inputRefs.current[Math.min(digits.length, 5)]?.focus();
      if (digits.length === 6 && !verifyMutation.isPending) {
        verifyMutation.mutate(digits);
      }
      return;
    }
    // A single typed character (or an empty string from deletion): accept only
    // digits or a clear; ignore stray non-digit keystrokes.
    if (value && !/^\d$/.test(value)) return;
    if (codeError) setCodeError(null);
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    const fullCode = newCode.join("");
    if (fullCode.length === 6 && !verifyMutation.isPending) {
      verifyMutation.mutate(fullCode);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    if (codeError) setCodeError(null);
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < 6; i++) {
      newCode[i] = pasted[i] || "";
    }
    setCode(newCode);
    if (pasted.length === 6) {
      verifyMutation.mutate(pasted);
    }
  };

  const handleUsePasswordInstead = () => {
    sessionStorage.setItem("padpulse_auth_pw_redirect", "1");
    navigate("/app/login");
  };

  if (sessionProbeState === "checking") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" data-testid="verify-session-checking">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-orange-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src={logoImg} alt="PadPulse" className="h-[50px] w-auto" data-testid="img-verify-logo" />
        </div>

        <Card className="w-full p-8 shadow-xl border border-gray-100 bg-white/80 backdrop-blur-sm">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-brand" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1 text-center" data-testid="text-verify-title">
            {isSignup ? "Verify Your Email" : "Enter Code"}
          </h1>
          <p className="text-gray-500 text-sm mb-6 text-center" data-testid="text-verify-subtitle">
            We sent a 6-digit code to <span className="font-medium text-gray-600">{email}</span>
          </p>
          <div className="flex gap-2.5 justify-center mb-2" onPaste={handlePaste}>
            {code.map((digit, i) => (
              <Input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                autoComplete={i === 0 ? "one-time-code" : "off"}
                className={`w-12 h-14 text-center text-2xl font-bold ${codeError ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "border-gray-200 focus:border-brand focus:ring-brand/20"}`}
                autoFocus={i === 0}
                data-testid={`input-code-${i}`}
              />
            ))}
          </div>
          {codeError && (
            <p className="text-xs text-red-600 mb-4 text-center" data-testid="error-verify-code">{codeError}</p>
          )}
          {!codeError && <div className="mb-4" />}
          {verifyMutation.isPending && (
            <div className="flex justify-center mb-4">
              <Loader2 className="h-6 w-6 animate-spin text-brand" />
            </div>
          )}
          <div className="text-center space-y-2">
            {hasPassword && (
              <button
                type="button"
                onClick={handleUsePasswordInstead}
                className="text-sm text-brand hover:underline block w-full"
                data-testid="button-use-password-instead"
              >
                Use password instead
              </button>
            )}
            <Button variant="ghost" size="sm" asChild className="text-gray-500 hover:text-gray-700" data-testid="button-back-login">
              <Link href="/app/login"><ArrowLeft className="h-4 w-4 mr-1" /> Back to login</Link>
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
